import { TriangleAlert, CircleCheck } from 'lucide-react';
import { useState } from 'react';
import type { ApprovalRequiredBlock } from '@netsuite-ai/types';
import { useAuthStore } from '../../store/auth.store';
import { resolveBlock } from '../../lib/chat-stream';

interface Props {
  block: ApprovalRequiredBlock;
}

export function ApprovalRequired({ block }: Props) {
  const status = block.status as string; // pending | applied | discarded | failed | superseded
  const [busy, setBusy] = useState(false);
  const role = useAuthStore((s) => s.role);
  const isReadOnly = role === 'support_readonly';

  async function handleApprove() {
    setBusy(true);
    await resolveBlock(block.id, 'approve');
    setBusy(false);
  }
  async function handleDeny() {
    setBusy(true);
    await resolveBlock(block.id, 'discard');
    setBusy(false);
  }

  if (status === 'applied' || status === 'approved') {
    return (
      <div className="bg-surface-success my-2 overflow-hidden px-4 py-5 shadow-sm">
        <div className="flex flex-col gap-2">
          <div className="text-success-text flex gap-2 font-bold">
            <div className="flex items-center justify-center">
              <CircleCheck size={16} />
            </div>
            <p>Approved</p>
            <span>-</span>
            <p>proceeding</p>
          </div>
          <p className="text-ink mt-1 text-sm">{block.description}</p>
        </div>
      </div>
    );
  }

  if (
    status === 'discarded' ||
    status === 'denied' ||
    status === 'superseded'
  ) {
    return (
      <div className="my-2 rounded border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm text-neutral-400">
        {status === 'superseded' ? 'No longer active' : 'Declined'}
      </div>
    );
  }

  return (
    <div className="border-surface-warm my-2 overflow-hidden rounded border bg-amber-50 shadow-sm dark:bg-amber-950/25">
      <div className="text-amber flex gap-3 px-4 py-3">
        <div>
          <TriangleAlert size={16} />
        </div>
        <div className="flex flex-col justify-center">
          <p className="text-sm font-semibold">Approval required</p>
          <p className="text-ink mt-1 text-sm">{block.description}</p>
          {status === 'failed' && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">
              The action failed — you can try approving again.
            </p>
          )}
        </div>
      </div>

      {!isReadOnly && (
        <div className="flex gap-2 px-4 py-2">
          <button
            onClick={handleDeny}
            disabled={busy}
            className="text-ink px-2 py-1.5 text-sm font-medium disabled:opacity-40"
          >
            Decline
          </button>
          <button
            onClick={handleApprove}
            disabled={busy}
            className="text-ink px-2 py-1.5 text-sm font-medium disabled:opacity-40"
          >
            Approve
          </button>
        </div>
      )}
    </div>
  );
}
